/**
 * 용량 룰 약사 검수 관리자 API 타입.
 *
 * 정본은 백엔드(`ymyd-prai-backend`)의 `dose-rules-admin.dto.ts`·`extraction/step3-schema.ts`와
 * `docs/api-specs/dose-review-api.md`다. 이 파일은 계획 문서
 * `docs/ai-discussions/20260909-dose-rules-약사검수-관리자화면.md` §3.0 계약을
 * 그대로 옮긴 것으로, **필드명·타입·상태값을 임의로 바꾸지 않는다.**
 * 2026-09-14 기준 수정(spec `2026-09-14-처방검토-룰추출-기준수정`)으로
 * 룰 status 4종(`gate_blocked` 추가)·세그먼트 새 키(`segment_kind`·`patient_condition`·
 * `label_daily_dose`·`merged_indications`·`citation.context_text`)·룰 최상위
 * `dropped_contraindications`·`provenance.criteria_version`/`gate_issues`가 들어왔다.
 * 새 키는 2026-09-14 재추출본에만 있고 옛 룰엔 없으므로 **전부 옵셔널**로 방어한다.
 *
 * - 응답 래퍼는 `{ error, data?, message? }`이며 **`error === 0`이 성공**이다(불리언 아님).
 * - 목록 페이징은 `data` 안에 `{ items, total, limit, offset }`로 온다(top-level meta 아님).
 * - 관리자 API는 중앙 룰(`pharmacy_user_id IS NULL`)만 다룬다.
 */

/**
 * 룰 상태값(룰 전용 `DoseRuleStatus`, 4종). 제품 상태(`DoseStatus`)는 3종 그대로다.
 * 목록 필터·상태 표시에 쓴다. 전이(PATCH) body는 `active`·`archived`만 받는다
 * (`UpdateDoseRuleStatusBody`). `gate_blocked`는 검사에 걸려 판정에서 빠진 룰이다.
 */
export type DoseRuleStatus = "pending_review" | "active" | "archived" | "gate_blocked";

/** 공통 응답 래퍼. `error === 0`이 성공. */
export interface DoseRuleApiResponse<T> {
  error: number;
  data?: T;
  message?: string;
}

/** 목록 조회 쿼리 (`FindDoseRulesQueryDto` + `PaginationDto`). */
export interface FindDoseRulesQuery {
  status?: DoseRuleStatus;
  /** 대표 룰(derived_from IS NULL)만 */
  representativeOnly?: boolean;
  /** 대상 제품에 검산 이슈(gate_issues)가 있는 룰만 */
  hasGateIssue?: boolean;
  /** 품목명/품목코드/표준코드/rule_id 부분 검색 */
  q?: string;
  limit?: number;
  offset?: number;
}

/** 목록 행 (`AdminDoseRuleListItem`). */
export interface AdminDoseRuleListItem {
  ruleId: string;
  status: string;
  type: string;
  isRepresentative: boolean;
  itemCode: string | null;
  standardCode: string | null;
  productName: string | null;
  hasGateIssue: boolean;
  updatedAt: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  /**
   * 현재 버전 번호(2026-09-24 백엔드 후행 추가). 목록 응답에 `versionCount`는 없지만
   * **은퇴 행이 보존되므로 현재 버전 번호 N = 총 버전 수 N**이라 이 값만으로 `vN` 배지를 만든다.
   * 필드가 없는 백엔드와 만날 수 있어 optional — `undefined > 1`은 false라 배지가 저절로 빠진다.
   */
  version?: number;
}

/** 목록 응답 (`AdminDoseRuleListData`). 페이징이 data 안에 담긴다. */
export interface AdminDoseRuleListData {
  items: AdminDoseRuleListItem[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * 세그먼트 내부 인용문. 백엔드 `dose_rules.content` JSON이 정본이므로
 * 모든 필드를 옵셔널로 방어한다.
 */
export interface DoseRuleCitation {
  section?: string | null;
  original_text?: string | null;
  evidence_level?: string | null;
  /** 코드가 채운 머리 문장(D3). 표시용 200자 이내. 옛 룰엔 없음. */
  context_text?: string | null;
  [key: string]: unknown;
}

/** 구간 종류(D1). 2026-09-14 재추출본에만 존재. 옛 룰엔 없다(값으로 추정하지 않는다 — D14). */
export type SegmentKind = "dose" | "contraindicated" | "not_established" | "not_recommended";

/** 환자 상태 조건(D4). 판정에서 빠지고 참고 문구에만 쓴다. */
export interface PatientCondition {
  kind: "renal" | "hepatic" | "other";
  text: string;
}

/** 원문 1일량(D6). 원문이 1일 총량을 N회로 나누라고 지시할 때만 채워진다. */
export interface LabelDailyDose {
  amount_min?: number | null;
  amount_max?: number | null;
  unit?: string | null;
  per_kg?: boolean | null;
  split_min?: number | null;
  split_max?: number | null;
  basis?: string | null;
}

/**
 * 용량 세그먼트. 조사 시점 필드를 옮겼으나 **백엔드 JSON이 정본**이라
 * 전부 옵셔널 접근한다(계획 §7 리스크 2). 신규 키는 2026-09-14 재추출본에만 있다.
 */
export interface DoseRuleSegment {
  dosemin?: number | null;
  dosemax?: number | null;
  daymax?: number | null;
  doseminperkg?: number | null;
  dosemaxperkg?: number | null;
  daymaxperkg?: number | null;
  age_min_months?: number | null;
  age_max_months?: number | null;
  weight_min_kg?: number | null;
  weight_max_kg?: number | null;
  is_default?: boolean | null;
  indication?: string | null;
  citation?: DoseRuleCitation | null;
  /** 구간 종류(D1). 옛 룰엔 없음 → undefined면 배지를 달지 않는다(D14). */
  segment_kind?: SegmentKind;
  /** 환자 상태 조건(D4). 없으면 null. */
  patient_condition?: PatientCondition | null;
  /** 원문 1일량(D6). 없으면 null. */
  label_daily_dose?: LabelDailyDose | null;
  /** 합쳐진 효능(D5). broad 전용·코드가 채움·2개 이상일 때만. */
  merged_indications?: string[];
  [key: string]: unknown;
}

/** 판정에 쓰지 않고 흔적만 남긴 금기 문장(D2). 룰 content/rawContent 최상위. */
export interface DroppedContraindication {
  text: string;
  reason: "pregnancy" | "lactation" | "disease" | "other";
}

/** 룰 content/rawContent 형상. `segments[]`가 핵심이나 형상은 백엔드 정본. */
export interface DoseRuleContent {
  segments?: DoseRuleSegment[];
  /** 제외한 금기(D2). 없으면 빈 배열. broad·raw 양쪽 최상위에 같은 값. */
  dropped_contraindications?: DroppedContraindication[];
  [key: string]: unknown;
}

/** 전개 룰 요약(상세 응답의 `derivedRules[]`). */
export interface DerivedRuleSummary {
  ruleId: string;
  status: string;
  standardCode: string | null;
  ratio: number | null;
}

/* ── 대상 제품(targetProduct)·룰 참조(productRef) 형상 (D-8) ──
 *
 * 정본은 백엔드 `dose-product.repository.ts`(SELECT alias → camelCase)와
 * `dose_products` JSON 컬럼(identity·dispense, snake_case), 룰 `product_ref` JSON(snake_case)다.
 * 현황 §3: 이전엔 `Record<string, unknown>`이라 없는 `productName`·camelCase `itemCode`
 * 오참조가 컴파일을 통과했다. 그 오참조를 타입이 잡도록 **넓은 인덱스 시그니처를 두지 않는다**.
 */

/** 함량 수량(값+단위). identity.ingredients[].strengths[].amount·per. */
export interface DoseQuantity {
  value?: number | null;
  unit?: string | null;
}

/** 1단위/1포장 함량 파생값. dispense.per_unit·per_pack (backend derive.ts). */
export interface DosePerUnit {
  value?: number | null;
  /** "mg/ml" | "mg/정" | "mg/g" 형태. */
  unit?: string | null;
  /** 사람이 검산 가능한 계산 사슬. */
  chain?: string | null;
}

/** 성분 함량 행. identity.ingredients[].strengths[] (backend step2-schema). */
export interface DoseProductStrength {
  basis?: string | null;
  amount?: DoseQuantity | null;
  per?: DoseQuantity | null;
  /** 1단위 함량 기준 행 여부(D-7 복원 규칙의 키). */
  is_ud_basis?: boolean;
  computed?: boolean;
  [key: string]: unknown;
}

/** 제품 성분. identity.ingredients[] (성분 + 함량 행). */
export interface DoseProductIngredient {
  core_ingredient?: string | null;
  herbal?: boolean;
  salt?: string | null;
  hydrate?: string | null;
  modifier?: string | null;
  strengths?: DoseProductStrength[];
  [key: string]: unknown;
}

/** identity JSON. dose_products.identity. */
export interface DoseProductIdentity {
  ingredients?: DoseProductIngredient[];
  dosage_form?: string | null;
  route?: string | null;
  [key: string]: unknown;
}

/** dispense JSON. dose_products.dispense (backend stamp.ts). */
export interface DoseProductDispense {
  prescribe_unit?: string | null;
  unit_tier?: string | null;
  unit_note?: string | null;
  insurance_unit_raw?: string | null;
  insurance_volume_ml?: number | null;
  insurance_spec?: string | null;
  spec_parsed?: unknown;
  per_unit?: DosePerUnit | null;
  per_pack?: DosePerUnit | null;
  [key: string]: unknown;
}

/**
 * 상세 응답 `targetProduct` — 백엔드 `DoseProduct`(camelCase alias) 형상(D-8).
 * `productCode`는 "기관 코드(무해석 복사)"라 보험코드(`insuranceCode`)와 다르다.
 */
export interface DoseTargetProduct {
  id?: number;
  productKey?: string | null;
  standardCode?: string | null;
  itemCode?: string | null;
  insuranceCode?: string | null;
  mainCode?: string | null;
  productCode?: string | null;
  ingredientCode?: string | null;
  atcCode?: string | null;
  identity?: DoseProductIdentity | null;
  dispense?: DoseProductDispense | null;
  unitTier?: string | null;
  dosageForm?: string | null;
  route?: string | null;
  /** yes | no | uncertain. */
  doseCheckEligible?: string | null;
  /** topical_free_dose | unit_mismatch | not_ingested | bsa_based | other. */
  doseCheckIneligibleReason?: string | null;
  checks?: Record<string, unknown> | null;
  status?: string | null;
  sourceFingerprint?: string | null;
  provenance?: Record<string, unknown> | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  deletedAt?: string | null;
}

/** 룰 `product_ref` JSON(snake_case). dose_rules.product_ref. */
export interface DoseProductRef {
  item_code?: string | null;
  standard_code?: string | null;
  prescribe_unit?: string | null;
  product_name?: string | null;
}

/** 룰이 걸리는 selector(D-6, Phase 2). axis는 AXIS_TO_KEY 문자열. */
export interface DoseRuleSelector {
  axis: string;
  label: string;
  value: string;
}

/** 같은 보험코드 공유 SKU(D-6, Phase 2). */
export interface DoseSharedSku {
  standardCode: string;
  productName: string | null;
}

/** 전개본 환산비(D-6, Phase 2). `provenance.baked`(불리언)와 다르다. */
export interface DoseBaked {
  ratio: number;
  from_unit: string;
  to_unit: string;
}

/** 상세 응답 (`AdminDoseRuleDetail`). */
export interface AdminDoseRuleDetail {
  ruleId: string;
  class: string;
  type: string;
  status: string;
  isRepresentative: boolean;
  derivedFrom: string | null;
  /** 병합본(broad) — 판정에 실제 쓰이는 값 */
  content: DoseRuleContent;
  /** 적응증별 원본. 전개본이면 null */
  rawContent: DoseRuleContent | null;
  productRef: DoseProductRef | null;
  /**
   * 룰 추출 출처. 재추출 단계 옵션(spec 2026-09-29)에서 신규 STEP3 산출에
   * `from_step: "step2" | "step3"`와 `step2_extracted_at`(보관본 시각, ISO)이 들어온다.
   * 옛 룰엔 없다(부재 = 알 수 없음). `Record`라 추출 정보 칸이 방어적으로 읽는다.
   */
  provenance: Record<string, unknown>;
  /** 대상 제품 dose_products.checks (gate_issues 등) */
  checks: Record<string, unknown> | null;
  targetProduct: DoseTargetProduct | null;
  derivedRules: DerivedRuleSummary[];
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  /** 룰이 걸리는 selector(D-6, Phase 2). 없으면 미제공. */
  selectors?: DoseRuleSelector[];
  /** 같은 보험코드 공유 SKU(D-6, Phase 2). */
  sharedSkus?: DoseSharedSku[];
  /** 공유 SKU가 상한(200)을 넘었는지(D-6, Phase 2). */
  sharedSkusTruncated?: boolean;
  /** 전개본 환산비(D-6, Phase 2). `provenance.baked`(불리언)와 다르다. */
  baked?: DoseBaked | null;
  /** 업체명(D-6, Phase 2, drugs.company_name 조인). */
  companyName?: string | null;
  /**
   * 이 품목의 `룰만 재추출` 가용성(재추출 단계 옵션 spec 2026-09-29, D-16a).
   * 옵셔널 — 옛 서버엔 없다(부재 = 룰만 재추출 미지원).
   */
  step2Snapshot?: DoseStep2Snapshot;
  /** 현재 버전 번호(버전 절 Phase 1). 없으면 메타가 `룰 버전: —`. */
  version?: number;
  /** 이 룰의 총 버전 수(은퇴 행 포함). 없으면 메타에 괄호를 만들지 않는다. */
  versionCount?: number;
  /**
   * 검수 메모 수 — **룰 일생 합산**이다.
   * 버전 목록 항목(`AdminDoseRuleVersionListItem.noteCount`)은 **그 행 한정**이라 뜻이 다르다.
   * 화면에 쓰지 않는다(버전 절 Phase 1 §3-3).
   */
  noteCount?: number;
}

/* ── 룰 버전 절 (Phase 1, spec `2026-09-25-처방검토-관리자룰화면-룰버전-UI`) ──
 *
 * 계약 정본은 그 spec의 `## 현황` 2절이다. 두 엔드포인트는 2026-09-24 백엔드(PR #112,
 * 마이그 v1.0.76)에 이미 들어가 있고 **이 repo는 소비만 한다.**
 *
 * **모든 필드를 optional로 선언한다**(계획 W-1) — "신규 필드는 전부 optional"이 이 작업의
 * 계약 불변식이고, `strictNullChecks`가 꺼져 있어(계획 S-1) optional과 nullable이 컴파일 시
 * 구별되지 않으므로 계약 문구를 그대로 따르는 쪽이 옳다. 실제 보호는 렌더의 런타임 방어다.
 */

/**
 * 버전 목록 항목 — `GET /api/v1/admin/dose-rules/:ruleId/versions` (계약 11필드).
 *
 * **현재 행을 포함**하고 서버가 `ORDER BY version DESC`로 준다 — **클라이언트 정렬을 넣지 않는다.**
 * `uk_dose_rules_current` 때문에 `retiredAt`이 빈 줄이 정확히 하나이며, 그것이 현재 버전 판별
 * 기준이다(은퇴는 `deleted_at`만 박고 `status`를 건드리지 않아 `active`로 은퇴한 행이 있다).
 *
 * 화면에 쓰는 것은 5개(`version`·`status`·`retiredAt`·`reviewedBy`·`reviewNote`)이고 `id`는
 * 노트 매칭 키다. `createdAt`·`reviewedAt`·`extractedAt`·`noteCount`는 **의도적으로 표시하지
 * 않으며**(줄이 6칸 고정), `criteriaVersion`은 화면의 기존 "기준 버전" 표기와 혼동되어 금지다
 * (계획 C-7). 빠뜨린 것이 아니다.
 */
export interface AdminDoseRuleVersionListItem {
  /** `dose_rules` 행 PK. 노트 매칭 키(`AdminDoseRuleNote.ruleRowId`)다. */
  id?: number;
  version?: number;
  status?: string;
  /** 은퇴 시각. **빈 값이면 현재 버전**이다. */
  retiredAt?: string | null;
  createdAt?: string | null;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  reviewNote?: string | null;
  criteriaVersion?: string | null;
  extractedAt?: string | null;
  /** **그 행 한정** 메모 수. 상세의 `noteCount`(룰 일생 합산)와 뜻이 다르다. */
  noteCount?: number;
}

/**
 * 버전 행(은퇴 포함) 당시 본문 — `GET /api/v1/admin/dose-rules/versions/:rowId` 응답(18필드).
 *
 * @spec:2026-09-29-처방검토-판정기록-조회화면 — 09-25 룰 버전 UI Phase 2 항목 10 공유분.
 * 먼저 머지되는 쪽이 만들고 나중 쪽은 재사용한다(D-12·D-21). **`AdminDoseRuleDetail`을 재사용하지
 * 않는다**(그 타입엔 버전 행에 없는 Phase 2 전용·룰 일생 합산 필드가 섞여 있다).
 * 판정 기록 상세가 당시 룰 본문을 `DoseRuleSegmentPanel`로 그릴 때 쓴다 — content·rawContent·
 * productRef(prescribe_unit)·provenance(criteria_version)·derivedFrom 매핑(D-21). nullable은
 * 09-25 항목 10 정의를 따른다.
 */
export interface AdminDoseRuleVersionDetail {
  /** `dose_rules` 행 PK(= rowId, `rules_json[].id`). */
  id: number;
  ruleId: string;
  /** 버전 번호. 은퇴 행 포함 조회라 null일 수 있다. */
  version: number | null;
  class: string;
  type: string;
  status: string;
  isRepresentative: boolean;
  derivedFrom: string | null;
  /** 병합본(broad) — 판정에 실제 쓰인 값. */
  content: DoseRuleContent;
  /** 적응증별 원본. 전개본이면 null. 있으면 거짓 "구버전" 배지가 뜨지 않는다(D-21). */
  rawContent: DoseRuleContent | null;
  productRef: DoseProductRef | null;
  /** 룰 추출 출처. `criteria_version` 등을 방어적으로 읽는다. */
  provenance: Record<string, unknown>;
  criteriaVersion: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  createdAt: string | null;
  /** 은퇴 시각. null이면 현재 버전. */
  retiredAt: string | null;
}

/**
 * 검수 메모/시스템 노트 — `GET /api/v1/admin/dose-rules/:ruleId/notes` (계약 9필드).
 *
 * 쿼리 `kind`를 넘기지 않고 전량을 받아 **`author`로 선별한다**(계획 E-1). 룰이 있고 메모가
 * 없으면 `[]`가 오고, `![]`는 false이므로 훅이 오검출로 throw하지 않는다.
 *
 * 은퇴 사유 칸은 4개(`ruleRowId`·`author`·`body`·`repeatCount`)만 쓰고 `lastAt`을 쓰지 않는다
 * (범위 밖 "검수 메모 이력 타임라인"으로 번진다, 계획 C-2). 쌓인 메모 목록(D-41)은 `kind`(=review
 * 선별)·`version`·`createdAt`·`authorName`·`ruleRowId`·`body`를 쓴다.
 */
export interface AdminDoseRuleNote {
  id?: number;
  /** 이 노트가 붙은 `dose_rules` 행 PK. 버전 줄 매칭은 **이 필드로만** 한다. */
  ruleRowId?: number;
  version?: number;
  kind?: string;
  /** `system:reextract` · `system:backfill-*` · 관리자 id 등. 선별 기준이다. */
  author?: string;
  /** 작성자 표시 이름(D-41, Phase 3 백엔드) — `users.name` 복호화, 없으면 null. `authorName ?? author`로 쓴다. */
  authorName?: string | null;
  body?: string;
  /** 서버가 같은 문구를 접은 횟수. 1보다 크면 사유 뒤에 `×N`. */
  repeatCount?: number;
  lastAt?: string | null;
  createdAt?: string | null;
}

/* ── 재추출 단계 옵션 (spec `2026-09-29-처방검토-보험코드-형제전파-재추출-단계옵션`) ──
 *
 * 계약 정본은 그 spec의 `## 변경 사항 > 계약`이다. **신규 필드는 전부 옵셔널**로 두어
 * 옛 서버(`step2Snapshot`·`fromStep` 부재)와도 공존한다(구버전 공존 표) — 필드가 없으면
 * 화면이 [룰만 재추출]을 disabled로 두고 "서버가 룰만 재추출을 지원하지 않습니다."를 보인다.
 */

/** 재추출 시작 단계. `step2`=현행 전체 재실행, `step3`=룰만. 생략 시 서버 기본 `step2`. */
export type FromStep = "step2" | "step3";

/** `step2Snapshot.unavailableReason` / 6112 `data.unavailableReason` 사유 6종. */
export type Step2SnapshotUnavailableReason =
  | "missing"
  | "step2_outdated"
  | "label_changed"
  | "product_drift"
  | "step2_blocked"
  | "not_eligible";

/**
 * 룰 상세 응답 `step2Snapshot` — 이 품목의 `룰만 재추출` 가용성(D-16a).
 * `available`이 false면 `unavailableReason`이 사유를, `extractedAt`은 Step 2 보관 시각(ISO)이다.
 */
export interface DoseStep2Snapshot {
  available?: boolean;
  unavailableReason?: Step2SnapshotUnavailableReason | null;
  extractedAt?: string | null;
}

/** 상태 전이 body (`UpdateDoseRuleStatusDto`). note는 500자 이내. */
export interface UpdateDoseRuleStatusBody {
  status: "active" | "archived";
  note?: string;
}

/** 상태 전이 응답. `cascaded` = 동반 전이된 전개본 수. */
export interface UpdateDoseRuleStatusResult {
  ruleId: string;
  status: string;
  cascaded: number;
}

/** 재추출 응답. `fromStep`은 신설(옛 서버엔 없음 → 성공 토스트 표기 생략). */
export interface ReextractResult {
  jobId: string;
  standardCode: string;
  itemCode: string | null;
  fromStep?: FromStep;
}

/** 집계 응답 (`AdminDoseRuleStats`). */
export interface AdminDoseRuleStats {
  total: number;
  byStatus: {
    pendingReview: number;
    active: number;
    archived: number;
    /** 차단(gate_blocked) 룰 수. 백엔드 stats `byStatus`도 camelCase. */
    gateBlocked: number;
  };
  representativeCount: number;
  derivedCount: number;
  withGateIssueCount: number;
}

/** 반려 사유(note) 최대 길이 — 계약상 500자 이내. */
export const DOSE_RULE_NOTE_MAX_LENGTH = 500;

/* ─────────────────────────────────────────────────────────────────────────
 * 추출 프롬프트 참조·보낸 전문 (spec 2026-09-30 Phase 2, D-4)
 * ───────────────────────────────────────────────────────────────────────── */

/**
 * `provenance.prompts.step2`/`step3` 참조(백엔드 `DosePromptRef` 9키 — D-4).
 * `step2`는 null 가능([룰만] 경로의 기능 이전 보관본). 기능 이전 룰은 `prompts` 키 자체가 없다.
 * `prompt_version`은 표시용 라벨(빈 문자열 가능), 정체 판정은 `template_sha256`.
 */
export interface DosePromptRef {
  prompt_id: number;
  prompt_version: string;
  prompt_type: string;
  model: string;
  template_sha256: string;
  system_sha256: string;
  user_sha256: string;
  tool_sha256: string;
  extracted_at: string;
}

/** `provenance.prompts` 형상(가산). 키 자체가 없을 수 있어 옵셔널. */
export interface DoseProvenancePrompts {
  step2?: DosePromptRef | null;
  step3?: DosePromptRef | null;
}

/** 보낸 전문 1건(`prompt-texts` 응답 `texts[]`). `body`는 저장 바이트 그대로. */
export interface AdminDosePromptText {
  sha256: string;
  body: string;
  byteLength: number;
}

/** `GET /prompt-texts` 응답 data. `missing`은 보관본이 없는 sha. */
export interface AdminDosePromptTextsData {
  texts: AdminDosePromptText[];
  missing: string[];
}
